/* === AgRadio & AgRadioGroup — Anagogia Design System === */

import { createContext, useContext } from 'react';
import styles from './AgRadio.module.css';

/* ── RadioGroup context ── */

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  size?: 'sm' | 'md';
  color?: 'primary' | 'secondary';
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

/* ── AgRadioGroup ── */

interface AgRadioGroupProps {
  /** Current selected value (controlled) */
  value?: string;
  /** Default selected value (uncontrolled) */
  defaultValue?: string;
  /** Called with the new value */
  onChange?: (value: string) => void;
  /** Shared name attribute for all radios in the group */
  name?: string;
  size?: 'sm' | 'md';
  color?: 'primary' | 'secondary';
  /** Layout direction */
  row?: boolean;
  className?: string;
  children: React.ReactNode;
}

let groupCounter = 0;

/**
 * Groupe de boutons radio à choix exclusif (contient des AgRadio).
 * @category Formulaires
 */
function AgRadioGroup({
  value,
  defaultValue,
  onChange,
  name,
  size = 'md',
  color = 'primary',
  row = false,
  className,
  children,
}: AgRadioGroupProps) {
  const groupName = name || `ag-radio-group-${++groupCounter}`;

  const rootCls = [
    styles.group,
    row ? styles.groupRow : styles.groupCol,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <RadioGroupContext.Provider value={{ name: groupName, value: value ?? defaultValue, onChange, size, color }}>
      <div role="radiogroup" className={rootCls}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

/* ── AgRadio ── */

interface AgRadioProps {
  /** Value for this radio option */
  value: string;
  /** Optional label rendered next to the radio */
  label?: React.ReactNode;
  size?: 'sm' | 'md';
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  className?: string;
}

const COLOR_MAP: Record<string, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
};

/**
 * Bouton radio individuel, à placer dans un AgRadioGroup.
 * @category Formulaires
 */
function AgRadio({
  value,
  label,
  size: sizeProp,
  color: colorProp,
  disabled = false,
  className,
}: AgRadioProps) {
  const group = useContext(RadioGroupContext);
  const size = sizeProp ?? group?.size ?? 'md';
  const color = colorProp ?? group?.color ?? 'primary';

  const isChecked = group ? group.value === value : undefined;

  const handleChange = () => {
    group?.onChange?.(value);
  };

  const wrapperCls = [
    styles.wrapper,
    disabled ? styles.disabled : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const circleCls = [styles.circle, styles[size], COLOR_MAP[color]]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={wrapperCls}>
      <span className={styles.radioContainer}>
        <input
          type="radio"
          className={styles.input}
          name={group?.name}
          value={value}
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
        />
        <span className={circleCls} aria-hidden="true">
          <span className={styles.dot} />
        </span>
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
}

export { AgRadioGroup, AgRadio };
export type { AgRadioGroupProps, AgRadioProps };
export default AgRadio;
